/**
 * `CallBackend` for the control panel: same-origin, behind the session cookie.
 *
 * A separate file from `backend.ts` so the interface stays importable by something
 * that must NOT pull in `@/api` — namely the embed bundle, which has no panel API
 * client, no `ApiError`, and no business shipping either to a stranger's website.
 * The interface is types only; this file is the one with the fetches in it.
 *
 * There is nothing clever here, which is the point: it is the code that used to be
 * inline in `CallSession.start()`, moved behind a name.
 */
import { endSession as apiEndSession, greetingAudioPath, sessions as sessionsApi } from '@/api'
import type { CreateSessionRequest, CreateSessionResponse } from '@voiceai/wire'
import type { CallBackend } from './backend'

/** Where the SDP proxy lives. Relative, so the panel stays same-origin — see the
 *  four rules at the top of api.ts. */
const SDP_PATH = (sessionId: string) => `/api/sessions/${encodeURIComponent(sessionId)}/sdp`

class CallHttpError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly detail: string,
  ) {
    super(message)
    this.name = 'CallHttpError'
  }
}

export const panelBackend: CallBackend = {
  createSession(req: CreateSessionRequest, signal: AbortSignal): Promise<CreateSessionResponse> {
    return sessionsApi.create(req, signal)
  },

  async exchangeSdp(sessionId: string, offerSdp: string, signal?: AbortSignal): Promise<string> {
    const res = await fetch(SDP_PATH(sessionId), {
      method: 'POST',
      headers: { 'content-type': 'application/sdp' },
      body: offerSdp,
      signal: signal ?? null,
    })
    const text = await res.text()
    if (!res.ok) {
      throw new CallHttpError(`the provider refused the offer (HTTP ${res.status})`, res.status, text)
    }
    // A 200 that is not SDP means something in front of the provider answered —
    // a proxy error page, most often — and `setRemoteDescription` would throw a
    // DOMException that reads like a WebRTC bug rather than an HTTP one.
    if (!text.startsWith('v=')) {
      throw new CallHttpError('the SDP answer does not look like SDP', res.status, text.slice(0, 200))
    }
    return text
  },

  endSession(sessionId: string, reason: string): void {
    // Swallows its own failure, per the contract in backend.ts. The DO's alarm and
    // the socket close both end the session anyway; this only decides whether it is
    // recorded as ENDED_NORMAL or ENDED_UNKNOWN, which is not worth failing a
    // teardown over.
    void apiEndSession(sessionId, reason, AbortSignal.timeout(5000)).catch(() => {})
  },

  async fetchGreetingClip(sha256: string, signal: AbortSignal): Promise<ArrayBuffer> {
    const res = await fetch(greetingAudioPath(sha256), { signal })
    if (!res.ok) throw new CallHttpError(`the greeting clip could not be fetched (HTTP ${res.status})`, res.status, '')
    return res.arrayBuffer()
  },
}
